import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import "./CompanyBranding.css";

const API_URL = "/api";

const CompanyBrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  company_name: "",
  commercial_name: "",
  business_name: "",
  logo: "",
  favicon: "",
  primary_color: "#1f5d7a",
  secondary_color: "var(--company-secondary)",
  login_background_color: "#eef2ff",
};

const isValidColor = (color) =>
  /^#[0-9A-F]{6}$/i.test(String(color || ""));

const getSafeColor = (color, fallback) =>
  isValidColor(color) ? color : fallback;

const getPublicAssetUrl = (path) => {
  if (!path) {
    return "";
  }

  return path.startsWith("http")
    ? path
    : `${API_URL}${path}`;
};

const getStoredCompanyInfo = () => {
  try {
    return JSON.parse(
      localStorage.getItem("company_info") || "{}"
    );
  } catch {
    return {};
  }
};

export function CompanyBrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => ({
    ...DEFAULT_BRANDING,
    ...getStoredCompanyInfo(),
  }));

  const applyBranding = useCallback((data = {}) => {
    const storedInfo = getStoredCompanyInfo();

    const companyName =
      data.commercial_name ||
      data.business_name ||
      data.company_name ||
      storedInfo.company_name ||
      DEFAULT_BRANDING.company_name;

    const normalizedBranding = {
      ...DEFAULT_BRANDING,
      ...storedInfo,
      ...data,
      company_name: companyName,
      primary_color: getSafeColor(
        data.primary_color || storedInfo.primary_color,
        DEFAULT_BRANDING.primary_color
      ),
      secondary_color: getSafeColor(
        data.secondary_color || storedInfo.secondary_color,
        DEFAULT_BRANDING.secondary_color
      ),
      login_background_color: getSafeColor(
        data.login_background_color ||
          storedInfo.login_background_color,
        DEFAULT_BRANDING.login_background_color
      ),
    };

    setBranding(normalizedBranding);

    localStorage.setItem(
      "company_info",
      JSON.stringify({
        ...storedInfo,
        ...normalizedBranding,
      })
    );

    const root = document.documentElement;

    root.style.setProperty(
      "--company-primary",
      normalizedBranding.primary_color
    );

    root.style.setProperty(
      "--company-secondary",
      normalizedBranding.secondary_color
    );

    root.style.setProperty(
      "--company-login-background",
      normalizedBranding.login_background_color
    );

    document.title = companyName
      ? `${companyName} | NetLedger`
      : "NetLedger";

    if (normalizedBranding.favicon) {
      let favicon = document.querySelector(
        "link[rel='icon']"
      );

      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }

      favicon.href = getPublicAssetUrl(
        normalizedBranding.favicon
      );
    }
  }, []);

  const loadBranding = useCallback(
    async (companySlug) => {
      if (!companySlug) {
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/companies/by-subdomain/${encodeURIComponent(
            companySlug
          )}/branding`
        );

        applyBranding(response.data);
      } catch (error) {
        console.error(
          "No fue posible cargar la identidad visual:",
          error
        );

        applyBranding(getStoredCompanyInfo());
      }
    },
    [applyBranding]
  );

  useEffect(() => {
    const currentPath = window.location.pathname
      .split("/")
      .filter(Boolean);

    const companySlug = currentPath[0];

    loadBranding(companySlug);
  }, [loadBranding]);

  const value = useMemo(
    () => ({
      branding,
      companyName:
        branding.commercial_name ||
        branding.business_name ||
        branding.company_name ||
        "Empresa",
      getPublicAssetUrl,
      applyBranding,
      refreshBranding: loadBranding,
    }),
    [applyBranding, branding, loadBranding]
  );

  return (
    <CompanyBrandingContext.Provider value={value}>
      {children}
    </CompanyBrandingContext.Provider>
  );
}

export function useCompanyBranding() {
  const context = useContext(CompanyBrandingContext);

  if (!context) {
    throw new Error(
      "useCompanyBranding debe usarse dentro de CompanyBrandingProvider."
    );
  }

  return context;
}